import { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Input,
  Select,
  Tag,
  MessagePlugin,
  Tabs,
  type PrimaryTableCol,
} from 'tdesign-react';
import {
  AddIcon, EditIcon, DeleteIcon, SearchIcon, PlusIcon,
} from 'tdesign-icons-react';
import {
  Building2, Sparkles, X, Award, Users, FileCheck, Target,
  HeartHandshake, Flag, Package, Video,
} from 'lucide-react';
import { enterpriseSiteAdminApi, tenantApi } from '../../api';
import ProductImageUploader from '../../../user/components/ProductImageUploader';
import ProductRichTextEditor from '../../../user/components/ProductRichTextEditor';

const { Option } = Select;

// ============ 通用组件 ============
function SectionCard({
  title,
  icon: Icon,
  children,
  action
}: {
  title: React.ReactNode;
  icon: any;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#1c5bff]" />
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

// ============ 类型定义 ============
interface ImageTextItem {
  id: string;
  image: string;
  title: string;
  description: string;
}

interface IconTextItem {
  id: string;
  icon: string;
  iconBgColor: string;
  title: string;
  description: string;
}

interface EnterpriseSiteData {
  id: number | null;
  tenantId: number | null;
  enterpriseName: string;
  enterpriseImage: string;
  siteUrl: string;
  introduction: string;
  customDisplayConfig: string[];
  cultureDisplay: ImageTextItem[];
  honors: ImageTextItem[];
  leadershipCare: ImageTextItem[];
  qualifications: ImageTextItem[];
  investmentProjects: IconTextItem[];
  enterpriseStrength: IconTextItem[];
  cooperation: ImageTextItem[];
  partyBuilding: ImageTextItem[];
  productIntros: ImageTextItem[];
  promoVideo: string;
  companyStrength ?: IconTextItem[] 
  cultureShow ?: IconTextItem[]
  partyBuildingNews ?: IconTextItem[]
  productIntro ?: IconTextItem[]
}

// ============ 常量 ============
const sectionOptions = [
  { value: 'cultureShow', label: '文化展示', icon: Sparkles },
  { value: 'honors', label: '荣誉资质', icon: Award },
  { value: 'leadershipCare', label: '领导关怀', icon: Users },
  { value: 'qualifications', label: '经营资质', icon: FileCheck },
  { value: 'investmentProjects', label: '投资项目', icon: Target },
  { value: 'companyStrength', label: '企业实力', icon: Building2 },
  { value: 'cooperation', label: '合作情况', icon: HeartHandshake },
  { value: 'partyBuildingNews', label: '党建动态', icon: Flag },
  { value: 'productIntro', label: '产品介绍', icon: Package },
  { value: 'promoVideo', label: '宣传视频', icon: Video },
];

const genId = () => Date.now().toString() + Math.random().toString(36).slice(2, 6);

const emptyImageTextItem = (): ImageTextItem => ({
  id: genId(), image: '', title: '', description: ''
});

const emptyIconTextItem = (): IconTextItem => ({
  id: genId(), icon: 'star', iconBgColor: '#1c5bff', title: '', description: ''
});

const emptyData = (): EnterpriseSiteData => ({
  id: null,
  tenantId: null,
  enterpriseName: '',
  enterpriseImage: '',
  siteUrl: '',
  introduction: '',
  customDisplayConfig: [],
  cultureDisplay: [],
  honors: [],
  leadershipCare: [],
  qualifications: [],
  investmentProjects: [],
  enterpriseStrength: [],
  cooperation: [],
  partyBuilding: [],
  productIntros: [],
  promoVideo: '',
});

// ============ 图片+标题+富文本 编辑组件 ============
function ImageRichEditor({
  items,
  onChange
}: {
  items: ImageTextItem[];
  onChange: (items: ImageTextItem[]) => void;
}) {
  const add = () => onChange([...items, emptyImageTextItem()]);
  const remove = (id: string) => onChange(items.filter(i => i.id !== id));
  const update = (id: string, field: keyof ImageTextItem, value: any) => {
    onChange(items.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="small" theme="primary" variant="outline" onClick={add}>
          <span className="flex items-center gap-1"><PlusIcon className="w-3 h-3" />添加</span>
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
          暂无内容，点击"添加"按钮新增
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div key={item.id} className="flex gap-3 items-start p-3 rounded-lg border border-gray-200 transition-all relative group">
              <button
                onClick={() => remove(item.id)}
                className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors opacity-0 group-hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="w-[50%] shrink-0">
                <ProductImageUploader
                  value={item.image}
                  onChange={(v) => update(item.id, 'image', v as string)}
                  simple
                />
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                  <Input
                    value={item.title}
                    onChange={(v) => update(item.id, 'title', v)}
                    placeholder="标题"
                    size="small"
                  />
                </div>
                <ProductRichTextEditor
                  value={item.description}
                  onChange={(v) => update(item.id, 'description', v)}
                  showHeight={false}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ 图标+背景色+标题+富文本 编辑组件 ============
function IconRichEditor({
  items,
  onChange
}: {
  items: IconTextItem[];
  onChange: (items: IconTextItem[]) => void;
}) {
  const add = () => onChange([...items, emptyIconTextItem()]);
  const remove = (id: string) => onChange(items.filter(i => i.id !== id));
  const update = (id: string, field: keyof IconTextItem, value: any) => {
    onChange(items.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="small" theme="primary" variant="outline" onClick={add}>
          <span className="flex items-center gap-1"><PlusIcon className="w-3 h-3" />添加</span>
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
          暂无内容，点击"添加"按钮新增
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {items.map((item, idx) => (
            <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-3 relative group">
              <button
                onClick={() => remove(item.id)}
                className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors opacity-0 group-hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                项目 #{idx + 1}
              </div>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">图标</label>
                  <Input
                    value={item.icon}
                    onChange={(v) => update(item.id, 'icon', v)}
                    placeholder="图标名称"
                    size="small"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">背景色</label>
                  <Input
                    value={item.iconBgColor}
                    onChange={(v) => update(item.id, 'iconBgColor', v)}
                    size="small"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">标题</label>
                  <Input
                    value={item.title}
                    onChange={(v) => update(item.id, 'title', v)}
                    placeholder="请输入标题"
                    size="small"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">描述</label>
                  <ProductRichTextEditor
                    value={item.description}
                    onChange={(v) => update(item.id, 'description', v)}
                    showHeight={false}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ API ↔ 数据 转换 ============
const fromApiImageTextItem = (item: any): ImageTextItem => ({
  id: item?.id ? String(item.id) : genId(),
  image: item?.image || '',
  title: item?.title || '',
  description: item?.description || '',
});

const fromApiIconTextItem = (item: any): IconTextItem => ({
  id: item?.id ? String(item.id) : genId(),
  icon: item?.icon || 'star',
  iconBgColor: item?.bgColor || '#1c5bff',
  title: item?.title || '',
  description: item?.description || '',
});

const toApiImageTextItem = (item: ImageTextItem) => ({
  image: item.image,
  title: item.title,
  description: item.description,
});

const toApiIconTextItem = (item: IconTextItem) => ({
  icon: item.icon,
  bgColor: item.iconBgColor,
  title: item.title,
  description: item.description,
});

const mapApiToData = (api: any): EnterpriseSiteData => {
  if (!api) return emptyData();
  // customDisplayConfig 可能是数组或对象
  let configArray: string[] = [];
  const customCfg = api.customDisplayConfig;
  if (Array.isArray(customCfg)) {
    configArray = customCfg;
  } else if (customCfg && typeof customCfg === 'object') {
    const sections = customCfg.selectedSections;
    if (Array.isArray(sections)) {
      configArray = sections;
    } else if (sections && typeof sections === 'object') {
      configArray = Object.keys(sections).filter(
        (k) => sections[k] === true || sections[k] === 1
      );
    }
  }

  // enterpriseImage 可能是数组或字符串
  const enterpriseImg = Array.isArray(api.enterpriseImage)
    ? (api.enterpriseImage[0] || '')
    : (api.enterpriseImage || '');

  return {
    id: api.id ?? null,
    tenantId: api.tenantId ?? null,
    enterpriseName: api.enterpriseName || '',
    enterpriseImage: enterpriseImg,
    siteUrl: api.siteUrl || '',
    introduction: api.introduction || '',
    customDisplayConfig: configArray,
    cultureDisplay: (api.cultureDisplay || []).map(fromApiImageTextItem),
    honors: (api.honors || []).map(fromApiImageTextItem),
    leadershipCare: (api.leadershipCare || []).map(fromApiImageTextItem),
    qualifications: (api.qualifications || []).map(fromApiImageTextItem),
    investmentProjects: (api.investmentProjects || []).map(fromApiIconTextItem),
    enterpriseStrength: (api.enterpriseStrength || []).map(fromApiIconTextItem),
    cooperation: (api.cooperation || []).map(fromApiImageTextItem),
    partyBuilding: (api.partyBuilding || []).map(fromApiImageTextItem),
    productIntros: (api.productIntros || []).map(fromApiImageTextItem),
    promoVideo: api.promoVideo || '',
  };
};

const mapDataToApi = (data: EnterpriseSiteData): any => {
  const result: any = {};
  if (data.id) result.id = data.id;
  if (data.tenantId) result.tenantId = data.tenantId;
  if (data.enterpriseName) result.enterpriseName = data.enterpriseName;
  if (data.enterpriseImage) result.enterpriseImage = [data.enterpriseImage];
  if (data.siteUrl) result.siteUrl = data.siteUrl;
  if (data.introduction) result.introduction = data.introduction;
  if ((data.customDisplayConfig || []).length > 0) result.customDisplayConfig = data.customDisplayConfig 

  const imgTextSections: [string, ImageTextItem[]][] = [
    ['cultureShow', data.cultureDisplay],
    ['honors', data.honors],
    ['leadershipCare', data.leadershipCare],
    ['qualifications', data.qualifications],
    ['cooperation', data.cooperation],
    ['partyBuildingNews', data.partyBuilding],
    ['productIntro', data.productIntros],
  ];
  for (const [key, items] of imgTextSections) {
    const mapped = items.filter(i => i.image || i.title || i.description).map(toApiImageTextItem);
    if (mapped.length > 0) result[key] = mapped;
  }

  const iconSections: [string, IconTextItem[]][] = [
    ['investmentProjects', data.investmentProjects],
    ['companyStrength', data.enterpriseStrength],
  ];
  for (const [key, items] of iconSections) {
    const mapped = items.filter(i => i.icon || i.title || i.description).map(toApiIconTextItem);
    if (mapped.length > 0) result[key] = mapped;
  }

  if (data.promoVideo) result.promoVideo = data.promoVideo;
  console.log(result,'resultresult---------------------')
  return result;
};

// ============ 列表行类型 ============
interface ListRow {
  id: number;
  tenantId: number;
  tenantName: string;
  enterpriseName: string;
  enterpriseImage: string;
  createTime: string;
  updateTime: string;
}

export default function WebsiteManage() {
  // ============ 列表状态 ============
  const [dataList, setDataList] = useState<ListRow[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const initializedRef = useRef(false);

  // ============ 弹窗状态 ============
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingData, setEditingData] = useState<EnterpriseSiteData>(emptyData());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');

  // ============ 加载租户列表 ============
  const loadTenants = async (keyword?: string) => {
    try {
      console.log(keyword,'keywordkeyword---------------------')
      const params: any = { pageNum: 1, pageSize: 50 };
      if (keyword) params.tenantName = keyword;
      console.log(params,'paramsparams---------------------')
      const res = await tenantApi.getPageList(params);
      if (res.code === 200 && res.data) { 
        setTenants(res.data.list || []);
      }
    } catch { /* ignore */ }
  };

  // ============ 加载列表 ============
  const loadData = async (pageNum?: number) => {
    setLoading(true);
    try {
      const res = await enterpriseSiteAdminApi.getPageList({
        pageNum: pageNum ?? pagination.current,
        pageSize: pagination.pageSize,
        enterpriseName: searchText || undefined,
      });
      if (res.code === 200 && res.data) {
        const list = res.data.list || [];
        const tenantMap = new Map(tenants.map(t => [t.id, t.tenantName]));
        setDataList(list.map((item: any) => ({
          id: item.id,
          tenantId: item.tenantId,
          tenantName: tenantMap.get(item.tenantId) || String(item.tenantId),
          enterpriseName: item.enterpriseName || '',
          enterpriseImage: Array.isArray(item.enterpriseImage) ? (item.enterpriseImage[0] || '') : (item.enterpriseImage || ''),
          createTime: item.createTime || '',
          updateTime: item.updateTime || '',
        })));
        setPagination(prev => ({
          ...prev,
          current: pageNum ?? prev.current,
          total: res.data.total || 0,
        }));
      }
    } catch (err: any) {
      MessagePlugin.error(err?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  // ============ 初始化 ============
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    loadTenants();
    loadData(1);
  }, []);

  // ============ 操作 ============
  const handleSearch = () => loadData(1);
  const handleReset = () => {
    setSearchText('');
    setTimeout(() => loadData(1), 0);
  };

  const handleCreate = () => {
    setEditingId(null);
    setEditingData(emptyData());
    setActiveTab('');
    setDialogVisible(true);
    loadTenants();
  };

  const handleEdit = async (id: number) => {
    setDialogLoading(true);
    try {
      const res = await enterpriseSiteAdminApi.getById(id);
      if (res.code === 200 && res.data) {
        const mapped = mapApiToData(res.data);
        setEditingData(mapped);
        setEditingId(id);
        setActiveTab((mapped.customDisplayConfig || [])[0] || '');
        setDialogVisible(true);
        loadTenants();
      } else {
        MessagePlugin.error(res.message || '获取数据失败');
      }
    } catch (err: any) {
      MessagePlugin.error(err?.message || '获取数据失败');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await enterpriseSiteAdminApi.delete(id);
      MessagePlugin.success('删除成功');
      loadData();
      setSelectedRowKeys([]);
    } catch (err: any) {
      MessagePlugin.error(err?.message || '删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      MessagePlugin.warning('请先选择要删除的记录');
      return;
    }
    try {
      await enterpriseSiteAdminApi.deleteBatch(selectedRowKeys.map(Number));
      MessagePlugin.success('批量删除成功');
      setSelectedRowKeys([]);
      loadData();
    } catch (err: any) {
      MessagePlugin.error(err?.message || '批量删除失败');
    }
  };

  const handleSave = async () => {
    if (!editingData.tenantId) {
      MessagePlugin.warning('请选择租户');
      return;
    }
    if (!editingData.enterpriseName.trim()) {
      MessagePlugin.warning('请输入企业名称');
      return;
    }

    setDialogLoading(true);
    try {
      const apiData = mapDataToApi(editingData);
      console.log(apiData,'apiDataapiData---------------------')
      const res = editingId
        ? await enterpriseSiteAdminApi.update(editingId, apiData)
        : await enterpriseSiteAdminApi.create(apiData);

      if (res.code === 200) {
        MessagePlugin.success(editingId ? '更新成功' : '创建成功');
        setDialogVisible(false);
        loadData();
      } else {
        MessagePlugin.error(res.message || '保存失败');
      }
    } catch (err: any) {
      MessagePlugin.error(err?.message || '保存失败');
    } finally {
      setDialogLoading(false);
    }
  };

  // ============ 表单辅助函数 ============
  const updateField = <K extends keyof EnterpriseSiteData>(key: K, value: EnterpriseSiteData[K]) => {
    setEditingData(prev => ({ ...prev, [key]: value }));
  };

  // 板块选择切换
  const toggleSection = (section: string) => {
    const current = editingData.customDisplayConfig || [];
    if (current.includes(section)) {
      updateField('customDisplayConfig', current.filter(s => s !== section));
      if (activeTab === section) setActiveTab(current.filter(s => s !== section)[0] || '');
    } else {
      const next = [...current, section];
      updateField('customDisplayConfig', next);
      if (!activeTab) setActiveTab(section);
    }
  };

  // Tab 拖拽排序
  const handleTabDragSort = (context: { current: string; target: string; currentIndex: number; targetIndex: number }) => {
    const sections = [...(editingData.customDisplayConfig || [])];
    const { currentIndex, targetIndex } = context;
    if (currentIndex === targetIndex) return;
    const [movedItem] = sections.splice(currentIndex, 1);
    sections.splice(targetIndex, 0, movedItem);
    updateField('customDisplayConfig', sections);
  };

  // Tab 删除
  const handleTabRemove = (options: { value: string; index: number }) => {
    const current = editingData.customDisplayConfig || [];
    const next = current.filter(s => s !== options.value);
    updateField('customDisplayConfig', next);
    if (activeTab === options.value) setActiveTab(next[0] || '');
  };

  // 渲染 Tab 内容
  const renderTabPanelContent = (section: string) => {
    switch (section) {
      case 'cultureShow':
        return <ImageRichEditor items={editingData.cultureDisplay} onChange={(v) => updateField('cultureShow', v)} />;
      case 'honors':
        return <ImageRichEditor items={editingData.honors} onChange={(v) => updateField('honors', v)} />;
      case 'leadershipCare':
        return <ImageRichEditor items={editingData.leadershipCare} onChange={(v) => updateField('leadershipCare', v)} />;
      case 'qualifications':
        return <ImageRichEditor items={editingData.qualifications} onChange={(v) => updateField('qualifications', v)} />;
      case 'investmentProjects':
        return <IconRichEditor items={editingData.investmentProjects} onChange={(v) => updateField('investmentProjects', v)} />;
      case 'companyStrength':
        return <IconRichEditor items={editingData.enterpriseStrength} onChange={(v) => updateField('companyStrength', v)} />;
      case 'cooperation':
        return <ImageRichEditor items={editingData.cooperation} onChange={(v) => updateField('cooperation', v)} />;
      case 'partyBuildingNews':
        return <ImageRichEditor items={editingData.partyBuilding} onChange={(v) => updateField('partyBuildingNews', v)} />;
      case 'productIntro':
        return <ImageRichEditor items={editingData.productIntros} onChange={(v) => updateField('productIntro', v)} />;
      case 'promoVideo':
        return (
          <div>
            <label className="text-xs text-gray-500 block mb-2">宣传视频</label>
            <ProductImageUploader
              value={editingData.promoVideo || ''}
              onChange={(v) => updateField('promoVideo', typeof v === 'string' ? v : '')}
              uploadType={2}
              simple
            />
            <div className="text-xs text-gray-400 mt-2">支持单个视频上传</div>
          </div>
        );
      default:
        return null;
    }
  };

  // ============ 表格列 ============
  const columns: PrimaryTableCol<ListRow>[] = [
    {
      colKey: 'id',
      title: 'ID',
      width: 80,
    },
    {
      colKey: 'enterpriseImage',
      title: '企业图片',
      width: 100,
      cell: ({ row }) => (
        <div className="w-16 h-16 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
          {row.enterpriseImage ? (
            <img src={row.enterpriseImage} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-gray-400 text-xs">无图</span>
          )}
        </div>
      ),
    },
    {
      colKey: 'enterpriseName',
      title: '企业名称',
      width: 180,
      ellipsis: true,
    },
    {
      colKey: 'tenantId',
      title: '租户',
      width: 160,
      cell: ({ row }) => (
        <Tag theme="primary" variant="light">{row.tenantName}</Tag>
      ),
    },
    {
      colKey: 'createTime',
      title: '创建时间',
      width: 180,
    },
    {
      colKey: 'updateTime',
      title: '更新时间',
      width: 180,
    },
    {
      colKey: 'action',
      title: '操作',
      width: 140,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            size="small"
            variant="text"
            theme="primary"
            onClick={() => handleEdit(row.id)}
          >
            <span className="flex items-center gap-1"><EditIcon className="w-3 h-3" />编辑</span>
          </Button>
          <Button
            size="small"
            variant="text"
            theme="danger"
            onClick={() => handleDelete(row.id)}
          >
            <span className="flex items-center gap-1"><DeleteIcon className="w-3 h-3" />删除</span>
          </Button>
        </div>
      ),
    },
  ];

  // ============ 渲染 ============
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">微官网管理</h1>
      </div>

      {/* 搜索区 */}
      <div className="flex items-center gap-3 mb-4">
        <Input
          value={searchText}
          onChange={setSearchText}
          placeholder="企业名称"
          style={{ width: 240 }}
        />
        <Button theme="primary" onClick={handleSearch}>
          <span className="flex items-center gap-1"><SearchIcon className="w-4 h-4" />搜索</span>
        </Button>
        <Button onClick={handleReset}>重置</Button>
        <div className="flex-1" />
        <Button theme="primary" onClick={handleCreate}>
          <span className="flex items-center gap-1"><AddIcon className="w-4 h-4" />新增</span>
        </Button>
        <Button theme="danger" onClick={handleBatchDelete}>
          <span className="flex items-center gap-1"><DeleteIcon className="w-4 h-4" />批量删除</span>
        </Button>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        data={dataList}
        rowKey="id"
        loading={loading}
        selectedRowKeys={selectedRowKeys}
        onSelectChange={(keys) => setSelectedRowKeys(keys as string[])}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showJumper: true,
          showPageSize: true,
          onChange: (pageInfo) => loadData(pageInfo.current),
        }}
        hover
      />

      {/* 新增/编辑弹窗 */}
      <Dialog
        visible={dialogVisible}
        onClose={() => setDialogVisible(false)}
        header={editingId ? '编辑微官网' : '新增微官网'}
        width={1000}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setDialogVisible(false)}>取消</Button>
            <Button theme="primary" onClick={handleSave} loading={dialogLoading}>
              保存
            </Button>
          </div>
        }
      >
        <div className="max-h-[75vh] overflow-y-auto pr-2 space-y-4">
          {/* 企业基本信息 */}
          <SectionCard title="企业基本信息" icon={Building2}>
            <div className="grid grid-cols-2 gap-4">
              {!editingId && (
                <div>
                  <label className="text-sm text-gray-500 block mb-2">选择租户<span className="text-red-500 ml-1">*</span></label>
                  <Select
                    value={editingData.tenantId !== null ? String(editingData.tenantId) : ''}
                    onChange={(v) => updateField('tenantId', v ? Number(v) : null)}
                    placeholder="请选择租户"
                    style={{ width: '100%' }}
                    filterable
                    onSearch={(keyword) => loadTenants(keyword)}
                  >
                    {tenants.map(t => (
                      <Option key={t.id} value={String(t.id)}>{t.tenantName}</Option>
                    ))}
                  </Select>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-500 block mb-2">企业名称<span className="text-red-500 ml-1">*</span></label>
                <Input
                  value={editingData.enterpriseName}
                  onChange={(v) => updateField('enterpriseName', v)}
                  placeholder="请输入企业名称"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-gray-500 block mb-2">企业网址</label>
                <Input
                  value={editingData.siteUrl}
                  onChange={(v) => updateField('siteUrl', v)}
                  placeholder="请输入企业网址，如 https://www.example.com"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-gray-500 block mb-2">企业图片</label>
                <ProductImageUploader
                  value={editingData.enterpriseImage}
                  onChange={(v) => updateField('enterpriseImage', v as string)}
                  simple
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-gray-500 block mb-2">企业介绍</label>
                <ProductRichTextEditor
                  value={editingData.introduction}
                  onChange={(v) => updateField('introduction', v)}
                />
              </div>
            </div>
          </SectionCard>

          {/* 企业信息自定义选择配置 */}
          <SectionCard title="企业信息自定义选择配置" icon={Sparkles}>
            <p className="text-xs text-gray-500 mb-4">企业信息自定义选择配置展示</p>

            {/* 选择区域 */}
            <div className="mb-4">
              <div className="text-xs text-gray-600 mb-2 font-medium">可选内容</div>
              <div className="grid grid-cols-5 gap-3">
                {sectionOptions.map(option => {
                  const isSelected = (editingData.customDisplayConfig || []).includes(option.value);
                  const IconComp = option.icon;
                  return (
                    <label
                      key={option.value}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-[#1c5bff] bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSection(option.value)}
                        className="accent-[#1c5bff]"
                      />
                      <IconComp className={`w-4 h-4 ${isSelected ? 'text-[#1c5bff]' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isSelected ? 'text-[#1c5bff] font-medium' : 'text-gray-700'}`}>
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Tabs 排列区域 */}
            {(editingData.customDisplayConfig || []).length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-600 font-medium">
                    已选内容 ({(editingData.customDisplayConfig || []).length} 项)
                  </div>
                  <span className="text-[10px] text-gray-400">支持拖拽排序、点击删除</span>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <Tabs
                    value={activeTab || (editingData.customDisplayConfig || [])[0] || ''}
                    onChange={(val) => setActiveTab(val as string)}
                    dragSort
                    scrollPosition="auto"
                    theme="card"
                    size="medium"
                    // destroyOnHide 不存在于 TabsProps 类型中，已移除
                    onDragSort={handleTabDragSort}
                    onRemove={handleTabRemove}
                  >
                    {editingData.customDisplayConfig.map((sectionValue) => {
                      const option = sectionOptions.find(o => o.value === sectionValue);
                      if (!option) return null;
                      const IconComp = option.icon;

                      const tabLabel = (
                        <span className="inline-flex items-center gap-1.5 py-1">
                          <IconComp className="w-3.5 h-3.5 text-[#1c5bff]" />
                          <span className="text-xs font-medium">{option.label}</span>
                        </span>
                      );

                      return (
                        <Tabs.TabPanel
                          key={sectionValue}
                          value={sectionValue}
                          label={tabLabel}
                          removable
                        >
                          <div className="p-3 bg-gray-50 rounded-lg max-h-[520px] overflow-y-auto">
                            {renderTabPanelContent(sectionValue)}
                          </div>
                        </Tabs.TabPanel>
                      );
                    })}
                  </Tabs>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50/50">
                暂未选择任何内容，请在上方选择需要展示的页面
              </div>
            )}
          </SectionCard>
        </div>
      </Dialog>
    </div>
  );
}
